import React, { Component } from 'react';
import withStyles from '@material-ui/core/styles/withStyles';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import ReactGA from 'react-ga4';
import Auth from '../services/auth';
import CodeMatrixDataAccess from '../services/codeMatrixDataAccess';

const codeMatrixDataAccess = new CodeMatrixDataAccess();

const styles = theme => ({
    root: theme.mixins.gutters({
        paddingTop: 16,
        paddingBottom: 16,
        marginTop: theme.spacing(3),
    }),
    card: {
        minWidth: 275,
    },
    tableWrapper: {
        width: '100%',
        overflowX: 'auto',
        marginTop: theme.spacing(2),
    },
    matrixTable: {
        borderCollapse: 'collapse',
        width: '100%',
        minWidth: 800,
        '& th, & td': {
            border: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(1),
            textAlign: 'left',
            whiteSpace: 'nowrap'
        },
        '& thead th': {
            background: theme.palette.background.default,
            position: 'sticky',
            top: 0,
            zIndex: 3
        }
    },
    stickyColumn: {
        position: 'sticky',
        left: 0,
        zIndex: 4,
        background: theme.palette.background.paper,
        boxShadow: '2px 0 4px rgba(0,0,0,0.05)'
    },
    loading: {
        padding: theme.spacing(2),
    },
    error: {
        color: theme.palette.error.main,
        padding: theme.spacing(2),
    }
});

class CodeMatrix extends Component {
    display = CodeMatrix.name

    constructor(props) {
        super(props);
        this.state = {
            rows: [],
            columns: [], 
            lastColumnName: null,
            loading: false,
            error: null
        };
    }

    onClassicView = () => {
        const auth = new Auth();
        ReactGA.event({
            action: 'Classic Portal',
            category: `${auth.getGaUser()}`
        });
    }

    componentDidMount() {
        this.loadMatrix();
    }

    formatCellValue = (val) => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'boolean') return val ? 'X' : '';
        if (typeof val === 'number') return val === 1 ? 'X' : String(val);
        if (typeof val === 'string') {
            const lower = val.trim().toLowerCase();
            if (lower === 'true' || lower === 't' || lower === '1') return 'X';
            if (lower === 'false' || lower === 'f' || lower === '0') return '';
            return val;
        }
        return String(val);
    }

    loadMatrix = async () => {
        this.setState({ loading: true, error: null });
        try {
            const data = await codeMatrixDataAccess.getCodeList();

            let rows = [];
            let columns = [];

            if (Array.isArray(data)) {
                rows = data;
            } else if (data && Array.isArray(data.rows)) {
                rows = data.rows;
            } else if (data && typeof data === 'object') {
                rows = [data];
            } else {
                rows = [];
            }

            if (rows.length > 0 && typeof rows[0] === 'object' && rows[0] !== null) {
                columns = Object.keys(rows[0]);
            }

            const { primaryColumn } = this.props;
            if (primaryColumn && columns.includes(primaryColumn)) {
                columns = [primaryColumn, ...columns.filter(c => c !== primaryColumn)];
            }

            let lastColumnName = null;
            if (columns.length > 0) {
                lastColumnName = columns[columns.length - 1];
                columns = columns.slice(0, -1); 
            }

            this.setState({ rows, columns, lastColumnName, loading: false });
        } catch (err) {
            console.error(err);
            this.setState({ error: 'Failed to load code array.', loading: false });
        }
    }

    renderTable() {
        const { classes } = this.props;
        const { columns, rows, lastColumnName } = this.state;

        if (!columns || columns.length === 0) {
            return <Typography>No hay datos para mostrar.</Typography>;
        }

        return (
            <div className={classes.tableWrapper}>
                <table className={classes.matrixTable}>
                    <thead>
                        <tr>
                            {columns.map((col, i) => (
                                <th
                                    key={`${col}-${i}`}
                                    className={i === 0 ? classes.stickyColumn : null}
                                >
                                    {col}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rIdx) => {
                            const tooltipValue = lastColumnName ? row[lastColumnName] : null;
                            const tooltipText = tooltipValue === null || tooltipValue === undefined
                                ? ''
                                : String(tooltipValue);
                            return (
                                <tr key={rIdx}>
                                    {columns.map((col, cIdx) => {
                                        const cellVal = row[col];
                                        const displayVal = this.formatCellValue(cellVal);
                                        // wrap first column cell in tooltip showing removed-last-column value
                                        if (cIdx === 0) {
                                            return (
                                                <td
                                                    key={`${rIdx}-${cIdx}`}
                                                    className={classes.stickyColumn}
                                                >
                                                    <Tooltip title={tooltipText} arrow>
                                                        <span>{displayVal}</span>
                                                    </Tooltip>
                                                </td>
                                            );
                                        }
                                        return (
                                            <td
                                                key={`${rIdx}-${cIdx}`}
                                            >
                                                {displayVal}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    render() {
        const { classes } = this.props;
        const { loading, error } = this.state;

        return (
            <div>
                <Typography variant="h4" gutterBottom>
                    Queue Matrix
                </Typography>
                <Card>
                    <CardContent>
                        <Grid container spacing={3} justify='space-between'>
                            <Grid item xs={12} lg={4} md={4}>
                                <Typography variant="h5" gutterBottom></Typography>
                            </Grid>
                            <Grid item xs={12} lg={4} md={4}></Grid>
                            <Grid item xs={12} lg={4} md={4}></Grid>
                        </Grid>

                        {loading && <Typography className={classes.loading}>Cargando matriz...</Typography>}
                        {error && <Typography className={classes.error}>{error}</Typography>}

                        {!loading && !error && this.renderTable()}
                    </CardContent>
                </Card>
            </div>
        );
    }
}

export default withStyles(styles, { withTheme: true })(CodeMatrix);